<script setup lang="ts">
interface Props {
  open: boolean,
  title: string,
  animationDirection?: 'horizontal' | 'vertical',
  closeOverride?: () => void
}
const props = withDefaults(defineProps<Props>(), {
  animationDirection: 'vertical',
  closeOverride: undefined
});

const emits = defineEmits<{
  'update:open': [value: boolean]
  'handleDismiss': []
}>();

interface Slots {
  default(): unknown
  subheader(): unknown
  headerButtons(): unknown
  footer(): unknown
}

defineSlots<Slots>();

function horizontalEnter(baseEl: HTMLElement) {
  const root = baseEl.shadowRoot;
  const backdrop = root?.querySelector('ion-backdrop');
  const modalWrapper = root?.querySelector('.modal-wrapper');

  if (backdrop === null || modalWrapper === null || backdrop === undefined || modalWrapper === undefined) {
    return;
  }

  const backdropAnimation = createAnimation()
    .addElement(backdrop)
    .fromTo('opacity', '0.01', 'var(--backdrop-opacity)');

  const wrapperAnimation = createAnimation()
    .addElement(modalWrapper)
    .keyframes([
      { offset: 0, opacity: '0', transform: 'translateX(100%)' },
      { offset: 1, opacity: '0.99', transform: 'translateX(0)' }
    ]);

  return createAnimation()
    .addElement(baseEl)
    .easing('ease-out')
    .duration(200)
    .addAnimation([backdropAnimation, wrapperAnimation]);
}

function horizontalLeave(baseEl: HTMLElement) {
  return horizontalEnter(baseEl)?.direction('reverse');
}

const getEnterAnimation = computed(() => props.animationDirection === 'horizontal' ? horizontalEnter : '');
const getLeaveAnimation = computed(() => props.animationDirection === 'horizontal' ? horizontalLeave : '');

const modalIsOpen = computed({
  get() {
    return props.open;
  },
  set(value) {
    emits('update:open', value);
  }
});

function close() {
  if (props.closeOverride === undefined) {
    modalIsOpen.value = false;
  } else {
    props.closeOverride();
  }
}
</script>

<template>
  <IonModal
    id="el"
    backdrop-dismiss
    show-backdrop
    :is-open="modalIsOpen"
    :enter-animation="getEnterAnimation"
    :leave-animation="getLeaveAnimation"
    @did-dismiss="$emit('handleDismiss'), modalIsOpen = false;"
  >
    <IonHeader class="ion-no-border">
      <IonToolbar>
        <IonButtons slot="start">
          <IonButton @click="close()">
            <IonIcon
              slot="icon-only"
              :class="{
                'fa-light fa-xmark': animationDirection === 'vertical',
                'fa-light fa-arrow-left': animationDirection === 'horizontal',
                'close-button': true
              }"
            />
          </IonButton>
        </IonButtons>
        <IonTitle> {{ title }} </IonTitle>
        <IonButtons slot="end">
          <slot name="headerButtons" />
        </IonButtons>
      </IonToolbar>
      <slot name="subheader" />
    </IonHeader>
    <slot />
  </IonModal>
</template>
